import { z } from 'zod';

// --------------------
// Common primitives
// --------------------
export const promptItemSchema = z.object({
  name: z.string(),
  relevantPromptText: z.string(),
});

export const rangeItemSchema = z.object({
  start_value: z.union([z.number(), z.null()]).nullable(),
  end_value: z.union([z.number(), z.null()]).nullable(),
  relevantPromptText: z.string(),
});

// --------------------
// Predefined enums (same as TalentDiscovery where applicable)
// --------------------
export const GenderEnum = z.enum(['Male', 'Female']);

export const EthnicityEnum = z.enum([
  'White',
  'Asian',
  'Hispanic',
  'Black',
  'Native',
  'Multiple',
]);

export const HighestDegreeEnum = z.enum([
  'High School',
  'Associate',
  'Bachelor',
  'MBA',
  'Master',
  'Doctor',
]);

export const SeniorityEnum = z.enum([
  'Entry Level',
  'Junior',
  'Associate',
  'Manager',
  'Director',
  'Executive',
  'Senior Executive',
]);

// Funding stage predefined list
export const FundingStageEnum = z.enum([
  'Angel/Pre-Seed',
  'Seed',
  'Series A',
  'Series B',
  'Series C',
  'Series D',
  'Series E+',
  'IPO',
]);

// Typed prompt items for predefined values
export const genderItemSchema = promptItemSchema.extend({ name: GenderEnum });
export const ethnicityItemSchema = promptItemSchema.extend({
  name: EthnicityEnum,
});
export const highestDegreeItemSchema = promptItemSchema.extend({
  name: HighestDegreeEnum,
});
export const seniorityItemSchema = promptItemSchema.extend({
  name: SeniorityEnum,
});
export const fundingStageItemSchema = promptItemSchema.extend({
  name: FundingStageEnum,
});

// --------------------
// Segment filter (people-side filters allowed inside segment.filter)
// --------------------
export const segmentPeopleFilterSchema = z.object({
  geography: z.array(promptItemSchema).nullable(),
  industry: z.array(promptItemSchema).nullable(),
  role: z.array(promptItemSchema).nullable(),

  seniority: z.array(seniorityItemSchema).nullable(),
  highest_degree: z.array(highestDegreeItemSchema).nullable(),
  gender: z.array(genderItemSchema).nullable(),
  ethnicity: z.array(ethnicityItemSchema).nullable(),
});

// One segment entry for a metric (headcount/inflow/outflow/etc.)
export const segmentItemSchema = z.object({
  start_value: z.union([z.number(), z.null()]).nullable(),
  end_value: z.union([z.number(), z.null()]).nullable(),
  relevantPromptText: z.string(),
  filter: segmentPeopleFilterSchema,
});

// All supported metric keys
export const segmentsSchema = z.object({
  headcount: z.array(segmentItemSchema).nullable(),
  inflow: z.array(segmentItemSchema).nullable(),
  outflow: z.array(segmentItemSchema).nullable(),
  hiring: z.array(segmentItemSchema).nullable(),
  attrition: z.array(segmentItemSchema).nullable(),
  growth: z.array(segmentItemSchema).nullable(),
  growth_yoy: z.array(segmentItemSchema).nullable(),
  salary: z.array(segmentItemSchema).nullable(),
  tenure: z.array(segmentItemSchema).nullable(),
});

// --------------------
// Primary selector (ONLY industry/geography/role)
// --------------------
export const primarySelectorSchema = z.object({
  industry: z.array(promptItemSchema).nullable(),
  geography: z.array(promptItemSchema).nullable(),
  role: z.array(promptItemSchema).nullable(),
});

// --------------------
// Company info selectors (company discovery filters)
// --------------------
export const companyInfoSelectorsSchema = z.object({
  competitors_of: z.array(promptItemSchema).nullable(), // postprocess: company -> rcid int[]
  is_public: z.boolean().nullable(),
  is_subsidiary: z.boolean().nullable(),

  hq_geography: z.array(promptItemSchema).nullable(), // postprocess: geo -> int[] (or split to hq_country/hq_region/hq_msa later)

  funding_stage: z.array(fundingStageItemSchema).nullable(), // predefined list
  year_founded: z.array(rangeItemSchema).nullable(),
  valuation: z.array(rangeItemSchema).nullable(),
  total_funding_amount: z.array(rangeItemSchema).nullable(),
  last_funding_amount: z.array(rangeItemSchema).nullable(),
  last_funding_year: z.array(rangeItemSchema).nullable(),

  keywords: z.array(z.string()).nullable(),

  founder_prev_company: z.array(promptItemSchema).nullable(), // postprocess: company -> rcid int[]
  founder_prev_university: z.array(promptItemSchema).nullable(), // postprocess: university -> int[]

  founder_gender: z.array(genderItemSchema).nullable(),
  founder_ethnicity: z.array(ethnicityItemSchema).nullable(),
});

export const entityInfoSelectorsSchema = z.object({
  company_info_selectors: companyInfoSelectorsSchema,
});

// --------------------
// Unknown filters schema (LLM must only put truly unsupported items here)
// --------------------
export const unknownFilterItemSchema = z.object({
  name: z.string(),
  relevantPromptText: z.string(),
  explanation: z.string(),
});

// --------------------
// Final LLM output schema (what the model must return)
// --------------------
export const companyDiscoveryAiOutputSchema = z.object({
  filters: z.object({
    primary_selector: primarySelectorSchema,
    entity_info_selectors: entityInfoSelectorsSchema,
    segments: segmentsSchema,
  }),
  unknownFilters: z.array(unknownFilterItemSchema),
});

// Useful inferred types
export type CompanyDiscoveryAiOutput = z.infer<
  typeof companyDiscoveryAiOutputSchema
>;

// --------------------
// For OpenAI (strict output schema)
// Allow raw text extraction for funding_stage and is_subsidiary
// --------------------
// Funding_stage schema that allows any string (for extraction only)
const fundingStageItemSchemaForOpenAI = promptItemSchema.extend({
  name: z.string(),
});

const companyInfoSelectorsForOpenAI =
  companyDiscoveryAiOutputSchema.shape.filters.shape.entity_info_selectors.shape.company_info_selectors.extend(
    {
      funding_stage: z.array(fundingStageItemSchemaForOpenAI).nullable(),
      // Allow either boolean or descriptive text for extraction
      is_subsidiary: z.union([z.boolean(), z.string()]).nullable(),
      is_public: z.union([z.boolean(), z.string()]).nullable(),
    }
  );

const entityInfoSelectorsForOpenAI =
  companyDiscoveryAiOutputSchema.shape.filters.shape.entity_info_selectors.extend(
    {
      company_info_selectors: companyInfoSelectorsForOpenAI,
    }
  );

export const companyDiscoveryAiFiltersForOpenAI =
  companyDiscoveryAiOutputSchema.shape.filters.extend({
    entity_info_selectors: entityInfoSelectorsForOpenAI,
  });

// Create final OpenAI schema
export const companyDiscoveryAiOutputSchemaForOpenAI = z.object({
  filters: companyDiscoveryAiFiltersForOpenAI,
  unknownFilters: z.array(unknownFilterItemSchema),
});
